import axios, { AxiosError, AxiosPromise } from 'axios';
import { useMutation, UseMutationResult, useQuery, UseQueryResult } from 'react-query';
import { E164Number } from 'libphonenumber-js/types';
import parsePhoneNumberFromString from 'libphonenumber-js';

import routes from 'routes';
import { RolesKey } from 'modules/types/util';
import {
  AddressBodyType,
  AddressType,
  UserFormType,
  UserSettingsType,
  UserStatsType,
  UserType,
} from 'api/types';
import { isHomeowner } from 'shared/utils/checkUserRole';

export function getUser(id: string): AxiosPromise<UserType> {
  return axios.get(routes.user(id));
}

type GetUsersRequestBody = {
  search?: string;
  page?: number;
  page_size?: number;
  sort_by?: string;
  direction?: 'ascending' | 'descending';
  internal_users?: boolean;
};

export function getUsers(body?: GetUsersRequestBody) {
  return axios.get(routes.users, { params: { ...body } });
}

type UnImpersonateUserResponse = {
  user: UserType;
  impersonator: UserType;
};

export async function unimpersonateUser(): AxiosPromise<UnImpersonateUserResponse> {
  await axios.post(routes.unimpersonate);
  const response = await axios.get(routes.me);

  return response;
}

export function impersonateUser(user_id: string) {
  return axios.post(routes.impersonate, { user_id });
}

export type UpdateUserRequestBody = {
  email?: string;
  archived?: boolean;
  role?: RolesKey;
  address?: Partial<AddressBodyType>;
};

export type UpdateUserPhoneRequestBody = {
  phone_number?: E164Number;
  enabled?: boolean;
  verified?: boolean;
};

export type UpdateUserSettingsRequestBody = {
  research_opt_in?: boolean;
  privacy_policy_accepted?: boolean;
  sms_opt_in?: boolean;
};
export type UpdateUserAddressRequestBody = {
  address?: Omit<AddressType, 'id' | 'archived'>;
};
export type UserPhoneSendVerificationCodeRequestBody = {
  phone_number: E164Number;
};
export type UserPhoneVerifyPhoneNumberRequestBody = {
  phone_number: E164Number;
  verification_code: string;
};

export function updateUserBillingAddress({
  user_id,
  body,
}: {
  user_id: string;
  body: UpdateUserAddressRequestBody;
}) {
  return axios.patch(routes.user(user_id), body);
}

export type UpdateUserAddressConfirmedRequestBody = {
  address?: Partial<AddressType>;
};

export function updateUserBillingAddressConfirmation({
  user_id,
  confirmed,
}: {
  user_id: string;
  confirmed: UpdateUserAddressConfirmedRequestBody;
}) {
  return axios.patch(routes.user(user_id), confirmed);
}

export function updateUser({ user_id, body }: { user_id: string; body: UpdateUserRequestBody }) {
  return axios.patch(routes.user(user_id), body);
}

export function updateUserPhone({
  user_id,
  body,
}: {
  user_id: string;
  body: UpdateUserPhoneRequestBody;
}) {
  const updatedBody = {
    ...body,
    ...(body.phone_number
      ? { phone_number: parsePhoneNumberFromString(body.phone_number, 'US')?.format('E.164') }
      : {}),
  };
  return axios.patch(routes.userPhone(user_id), updatedBody);
}

type UseUpdateUserBody = {
  onSuccess?: (data: UserType, variables: { user_id: string; body: UpdateUserRequestBody }) => void;
  onError?: (error: AxiosError, variabes: { user_id: string; body: UpdateUserRequestBody }) => void;
};

export const useUpdateUser = ({
  onSuccess,
  onError,
}: UseUpdateUserBody): UseMutationResult<
  UserType,
  AxiosError,
  { user_id: string; body: UpdateUserRequestBody }
> =>
  useMutation(
    ({ user_id, body }) => axios.patch(routes.user(user_id), body).then((res) => res.data),
    {
      onSuccess,
      onError,
    },
  );

type UseUpdateUserSettingsBody = {
  onSuccess?: (
    data: UserSettingsType,
    variables: { user_id: string; body: UpdateUserSettingsRequestBody },
  ) => void;
  onError?: (
    error: AxiosError,
    variabes: { user_id: string; body: UpdateUserSettingsRequestBody },
  ) => void;
};
export const useUpdateUserSettings = ({
  onSuccess,
  onError,
}: UseUpdateUserSettingsBody): UseMutationResult<
  UserSettingsType,
  AxiosError,
  { user_id: string; body: UpdateUserSettingsRequestBody }
> =>
  useMutation(
    ({ user_id, body }) => axios.patch(routes.userSettings(user_id), body).then((res) => res.data),
    {
      onSuccess,
      onError,
    },
  );

export const useUpdateUserAndPhone = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: AxiosError) => void;
}) => {
  const updateUserMutation = useMutation(
    ({ user_id, body }: { user_id: string; body: UpdateUserRequestBody }) =>
      axios.patch(routes.user(user_id), body).then((res) => res.data),
  );

  const updateUserPhoneMutation = useMutation(
    ({ user_id, body }: { user_id: string; body: UpdateUserPhoneRequestBody }) => {
      const updatedBody = {
        ...body,
        ...(body.phone_number
          ? { phone_number: parsePhoneNumberFromString(body.phone_number, 'US')?.format('E.164') }
          : {}),
      };
      return axios.patch(routes.userPhone(user_id), updatedBody).then((res) => res.data);
    },
  );
  const mutate = async ({
    user_id,
    userBody,
    phoneBody,
  }: {
    user_id: string;
    userBody?: UpdateUserRequestBody;
    phoneBody?: UpdateUserPhoneRequestBody;
  }) => {
    try {
      const promises = [];
      if (
        userBody &&
        Object.keys(userBody).some(
          (key) => userBody[key as keyof UpdateUserRequestBody] !== undefined,
        )
      ) {
        promises.push(updateUserMutation.mutateAsync({ user_id, body: userBody }));
      }
      if (
        phoneBody &&
        Object.keys(phoneBody).some(
          (key) => phoneBody[key as keyof UpdateUserPhoneRequestBody] !== undefined,
        )
      ) {
        promises.push(updateUserPhoneMutation.mutateAsync({ user_id, body: phoneBody }));
      }

      await Promise.all(promises);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      if (onError) {
        onError(error as AxiosError);
      }
    }
  };

  return {
    mutate,
    isLoading: updateUserMutation.isLoading || updateUserPhoneMutation.isLoading,
    isError: updateUserMutation.isError || updateUserPhoneMutation.isError,
  };
};

type UseInviteUserBody = {
  onSuccess?: (data: UserType) => void;
  onError?: (error: AxiosError<ErrorResponse>) => void;
};

type ErrorResponse = {
  detail?: string | string[];
  default_code?: string;
  error_codes?: string;
};

export const useInviteUser = ({
  onSuccess,
  onError,
}: UseInviteUserBody): UseMutationResult<unknown, AxiosError<ErrorResponse>, UserFormType> =>
  useMutation(
    (body: UserFormType) => {
      const updatedBody = {
        ...body,
        is_admin_invite: !isHomeowner(body.role),
        ...(body.phone.phone_number
          ? {
              phone: {
                phone_number: parsePhoneNumberFromString(body.phone.phone_number, 'US')?.format(
                  'E.164',
                ),
              },
            }
          : {}),
      };
      return axios.post(routes.invite, updatedBody).then((res) => res.data);
    },
    {
      onSuccess,
      onError,
    },
  );

type UseImpersonateUserBody = {
  onSuccess?: (data: { user: UserType; impersonator: UserType }) => void;
  onError?: (error: AxiosError) => void;
};

export const useImpersonateUser = ({
  onSuccess,
  onError,
}: UseImpersonateUserBody): UseMutationResult<unknown, unknown, { user_id: string }> =>
  useMutation((body) => axios.post(routes.impersonate, body).then((res) => res.data), {
    onSuccess,
    onError,
  });

export const useResendClaimAccountEmail = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: AxiosError) => void;
}): UseMutationResult<unknown, unknown, { email: string }> =>
  useMutation(
    ({ email }) =>
      axios.post(routes.sendClaimAccountEmail, { email, is_resend: true }).then((res) => res.data),
    {
      onSuccess,
      onError,
    },
  );

type UseSendPhoneVerificationCodeBody = {
  onSuccess?: (
    data: unknown,
    variables: { user_id: string; body: UserPhoneSendVerificationCodeRequestBody },
  ) => void;
  onError?: (
    error: AxiosError,
    variabes: { user_id: string; body: UserPhoneSendVerificationCodeRequestBody },
  ) => void;
};

export const useSendPhoneVerificationCode = ({
  onSuccess,
  onError,
}: UseSendPhoneVerificationCodeBody): UseMutationResult<
  unknown,
  AxiosError,
  { user_id: string; body: UserPhoneSendVerificationCodeRequestBody }
> =>
  useMutation(
    ({ user_id, body }) =>
      axios.post(routes.sendPhoneVerificationCode(user_id), body).then((res) => res.data),
    {
      onSuccess,
      onError,
    },
  );

type UseVerifyPhoneNumberBody = {
  onSuccess?: (
    data: unknown,
    variables: { user_id: string; body: UserPhoneVerifyPhoneNumberRequestBody },
  ) => void;
  onError?: (
    error: AxiosError,
    variabes: { user_id: string; body: UserPhoneVerifyPhoneNumberRequestBody },
  ) => void;
};

export const useVerifyPhoneNumber = ({
  onSuccess,
  onError,
}: UseVerifyPhoneNumberBody): UseMutationResult<
  unknown,
  AxiosError,
  { user_id: string; body: UserPhoneVerifyPhoneNumberRequestBody }
> =>
  useMutation(
    ({ user_id, body }) =>
      axios.post(routes.verifyPhoneNumber(user_id), body).then((res) => res.data),
    {
      onSuccess,
      onError,
    },
  );

/* 
|
|       Bottom API CALLS are ONLY for admin dashboard       
|
*/

export const useGetUserStatistics = (): UseQueryResult<UserStatsType, unknown> =>
  useQuery(['userStatistics'], () => axios.get(routes.userStatistics).then((res) => res.data));
